import { useState, useEffect, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { Card } from "@/components/ui";
import Button from "@/components/ui/Button";
import { useAuthStore } from "@/store/authStore";
import { setSoundEnabled, sfxClick } from "@/utils/soundEngine";
import toast from "react-hot-toast";
import clsx from "clsx";
import { launchCore } from "@/utils/launchCore";
import { useLearnerModel } from "@/components/learn/useLearnerModel";
import { WORLDS } from "@/flow-content/curriculum";

/* ─── SVG Icons ──────────────────────────────────────────────────────────── */
function ToggleIcon({ on }) {
  return on
    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#22c55e" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
    : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      onClick={() => { if (!disabled) { sfxClick(); onChange(!checked); } }}
      className={clsx(
        "relative w-11 h-6 rounded-full transition-colors duration-300 flex-shrink-0 focus:outline-none",
        checked ? "bg-violet-600" : "bg-nexus-s2 border border-nexus-border",
        disabled && "opacity-30 cursor-not-allowed"
      )}
    >
      <span className={clsx(
        "absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 flex items-center justify-center",
        checked ? "left-6" : "left-1"
      )}>
        <ToggleIcon on={checked}/>
      </span>
    </button>
  );
}

function SettingRow({ label, sub, value, onChange, disabled }) {
  return (
    <div className={clsx("flex items-center justify-between py-2.5", disabled && "opacity-40")}>
      <div className="pr-4">
        <p className="text-sm font-bold text-white">{label}</p>
        {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
      </div>
      <Toggle checked={value} onChange={onChange} disabled={disabled}/>
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div className="bg-nexus-sf border border-nexus-border rounded-2xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-nexus-border">
        <span className="text-gray-500">{icon}</span>
        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">{title}</h3>
      </div>
      <div className="px-4 divide-y divide-nexus-border/50">
        {children}
      </div>
    </div>
  );
}

/* ─── Lernwelten: Alter (drives the adaptive learner model) ─────────────── */
function LearnerAgeSection() {
  const learner = useLearnerModel();
  const [open, setOpen] = useState(false);
  const age = learner.age || 12;

  const bump = (delta) => {
    const next = Math.max(8, Math.min(16, age + delta));
    if (next !== age) { sfxClick(); learner.setAge(next); toast.success(`Alter auf ${next} Jahre gesetzt — Lernwelten passen sich an ✨`); }
  };

  return (
    <SectionCard title="Lernwelten" icon={
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10L12 5 2 10l10 5 10-5z"/><path d="M6 12v5c0 1 2.5 2.5 6 2.5s6-1.5 6-2.5v-5"/></svg>
    }>
      <div className="flex items-center justify-between py-2.5">
        <div className="pr-4">
          <p className="text-sm font-bold text-white">Dein Alter</p>
          <p className="text-[11px] text-gray-500 mt-0.5">Steuert, wie schwer & altersgerecht deine Aufgaben sind</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => bump(-1)} disabled={age <= 8}
            className="w-8 h-8 rounded-xl bg-nexus-s2 border border-nexus-border text-white font-black flex items-center justify-center disabled:opacity-30 active:scale-90 transition-transform">−</button>
          <span className="w-10 text-center text-lg font-black text-violet-300">{age}</span>
          <button onClick={() => bump(1)} disabled={age >= 16}
            className="w-8 h-8 rounded-xl bg-nexus-s2 border border-nexus-border text-white font-black flex items-center justify-center disabled:opacity-30 active:scale-90 transition-transform">+</button>
        </div>
      </div>

      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between py-2.5 text-left">
        <p className="text-sm font-bold text-white">Meine Lernstufen pro Fach</p>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#6b7280" strokeLinecap="round" className={clsx("transition-transform", open && "rotate-180")}><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && (
        <div className="pb-3 grid grid-cols-2 gap-2">
          {WORLDS.map((w) => {
            const lvl = learner.subjectLevel(w.id);
            return (
              <div key={w.id} className="flex items-center justify-between rounded-xl bg-nexus-s2 border border-nexus-border px-3 py-2">
                <span className="text-[11px] font-bold text-gray-400 truncate pr-2">{w.emoji} {w.name}</span>
                <span className="text-xs font-black flex-shrink-0" style={{ color: w.color }}>Lv {lvl}</span>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

/* ─── Shared: PendingInviteCard ─────────────────────────────────────────── */
function PendingInviteCard({ inv, onCancel, onToggleCode, showingCode, cancelling, label }) {
  const daysLeft = Math.max(0, Math.ceil((new Date(inv.expiresAt) - Date.now()) / 86400000));
  return (
    <div className="rounded-2xl overflow-hidden border border-amber-500/20 bg-amber-950/15">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-9 h-9 rounded-xl bg-amber-900/30 border border-amber-600/30 flex items-center justify-center flex-shrink-0">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#fbbf24" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-black text-amber-300">{label || "Einladung ausstehend"}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">
            Läuft ab in {daysLeft} Tag{daysLeft !== 1 ? "en" : ""}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onToggleCode}
            className="px-2.5 py-1.5 rounded-xl bg-violet-900/30 border border-violet-500/25 text-violet-300 text-[10px] font-black hover:bg-violet-800/40 transition-all active:scale-95"
          >
            {showingCode ? "Verstecken" : "Code zeigen"}
          </button>
          <button
            onClick={() => onCancel(inv.id)}
            disabled={cancelling}
            className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-600 hover:text-red-400 hover:bg-red-900/30 transition-all duration-150 active:scale-90"
            title="Einladung zurückziehen"
          >
            {cancelling
              ? <div className="w-3.5 h-3.5 border-2 border-red-500/50 border-t-red-500 rounded-full animate-spin"/>
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
            }
          </button>
        </div>
      </div>

      {/* Code reveal panel */}
      {showingCode && (
        <div className="mx-3 mb-3 rounded-xl bg-nexus-bg border border-violet-500/25 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-violet-500/15">
            <p className="text-[10px] text-gray-500 font-medium">
              📲 Gib diesen Code weiter. Er/Sie gibt ihn beim Registrieren ein:
            </p>
          </div>
          <div className="px-4 py-3">
            <div
              className="text-center text-2xl font-black tracking-[0.25em] py-3 px-4 rounded-xl cursor-pointer select-all transition-all hover:scale-[1.02] active:scale-95"
              style={{
                background: "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(6,182,212,0.08))",
                border: "1px solid rgba(139,92,246,0.3)",
                color: "#c4b5fd",
                textShadow: "0 0 20px rgba(139,92,246,0.4)",
                fontVariantNumeric: "tabular-nums",
              }}
              onClick={() => { sfxClick(); navigator.clipboard?.writeText(inv.code); toast.success("Code kopiert! 📋"); }}
              title="Tippen zum Kopieren"
            >
              {inv.code}
            </div>
            <p className="text-[9px] text-center text-gray-700 mt-2 font-medium">
              Tippe auf den Code zum Kopieren
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Add Connection Modal ──────────────────────────────────────────────── */
const INVITE_ROLES = [
  { value: "parent",        label: "Elternteil",     emoji: "👨‍👩‍👧", desc: "Sehen deinen Fortschritt" },
  { value: "mentor",        label: "Mentor",          emoji: "🧑‍🏫", desc: "Unterstützt dein Lernen" },
  { value: "therapist",     label: "Therapeut",       emoji: "🩺",  desc: "Fachliche Begleitung" },
  { value: "lernbegleiter", label: "Lernbegleiter",   emoji: "📚",  desc: "Persönliche Lernbegleitung" },
  { value: "other",         label: "Sonstiges",       emoji: "🤝",  desc: "Andere Vertrauensperson" },
];

function AddConnectionModal({ open, onClose, onCreated }) {
  const [generatedCode, setGeneratedCode] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const createCode = useMutation({
    mutationFn: (role) => {
      if (role === "parent") {
        return api.post("/codes/parent-invite").then((r) => r.data);
      }
      return api.post("/codes/connection-invite", { connectedRole: role }).then((r) => r.data);
    },
    onSuccess: (data, role) => {
      sfxClick();
      setSelectedRole(role);
      setGeneratedCode(data.code);
      onCreated?.();
      toast.success("Einladungscode erstellt! 🎉");
    },
    onError: (err) => toast.error(err.response?.data?.error || "Code konnte nicht erstellt werden"),
  });

  const handleClose = () => {
    setGeneratedCode(null);
    setSelectedRole(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="w-full max-w-sm bg-nexus-sf border border-nexus-border rounded-3xl overflow-hidden shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-nexus-border">
          <h2 className="font-black text-base">🔗 Jemanden einladen</h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition-all"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="p-4 space-y-3">
          {generatedCode ? (
            /* Generated code display */
            <div className="space-y-3">
              <div className="text-center">
                <p className="text-sm font-black text-white">Code erstellt!</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Teile diesen Code mit deiner {INVITE_ROLES.find(r => r.value === selectedRole)?.label || "Person"}
                </p>
              </div>
              <div
                className="text-center text-2xl font-black tracking-[0.25em] py-4 px-4 rounded-2xl cursor-pointer select-all transition-all hover:scale-[1.02] active:scale-95"
                style={{
                  background: "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(6,182,212,0.08))",
                  border: "1px solid rgba(139,92,246,0.3)",
                  color: "#c4b5fd",
                  textShadow: "0 0 20px rgba(139,92,246,0.4)",
                  fontVariantNumeric: "tabular-nums",
                }}
                onClick={() => { sfxClick(); navigator.clipboard?.writeText(generatedCode.code || generatedCode); toast.success("Code kopiert! 📋"); }}
                title="Tippen zum Kopieren"
              >
                {generatedCode.code || generatedCode}
              </div>
              <p className="text-[10px] text-center text-gray-600">Tippe auf den Code zum Kopieren</p>
              <button
                onClick={handleClose}
                className="w-full py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-black transition-all active:scale-95"
              >
                Fertig
              </button>
            </div>
          ) : (
            /* Role picker */
            <>
              <p className="text-xs text-gray-500 text-center">Wähle die Rolle der Person, die du einladen möchtest:</p>
              <div className="grid grid-cols-1 gap-2">
                {INVITE_ROLES.map((role) => (
                  <button
                    key={role.value}
                    onClick={() => createCode.mutate(role.value)}
                    disabled={createCode.isPending}
                    className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-nexus-border hover:border-violet-500/40 bg-nexus-s2 hover:bg-violet-950/20 transition-all active:scale-[0.98] text-left disabled:opacity-50"
                  >
                    <span className="text-2xl flex-shrink-0">{role.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-white">{role.label}</p>
                      <p className="text-[10px] text-gray-500">{role.desc}</p>
                    </div>
                    {createCode.isPending && createCode.variables === role.value ? (
                      <div className="w-4 h-4 border-2 border-violet-500/30 border-t-violet-400 rounded-full animate-spin flex-shrink-0" />
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#6b7280" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Connection Section (students) ────────────────────────────────────── */
function ParentSlot({ index, parent, onUnlink, unlinking }) {
  const filled = !!parent;
  return (
    <div className={clsx(
      "relative flex items-center gap-3 rounded-2xl px-4 py-3 transition-all duration-200",
      filled
        ? "bg-gradient-to-r from-violet-950/50 to-violet-900/20 border border-violet-500/25"
        : "border-2 border-dashed border-white/6 hover:border-violet-500/20"
    )}>
      <div className={clsx(
        "w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0",
        filled ? "bg-violet-500/20 text-violet-300" : "bg-white/4 text-gray-600"
      )}>
        {filled ? (parent.avatar || "👤") : index + 1}
      </div>

      <div className="flex-1 min-w-0">
        {filled ? (
          <>
            <p className="text-sm font-black text-white truncate">{parent.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ boxShadow: "0 0 4px #4ade80" }}/>
              <p className="text-[10px] text-gray-500 font-medium">
                Verbunden · {new Date(parent.linkedAt).toLocaleDateString("de-DE")}
              </p>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm font-bold text-gray-500">Elternteil {index + 1}</p>
            <p className="text-[10px] text-gray-700 mt-0.5">Noch nicht verbunden</p>
          </>
        )}
      </div>

      {filled && (
        <button
          onClick={() => onUnlink(parent.linkId)}
          disabled={unlinking}
          title="Elternteil entfernen"
          className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-600 hover:text-red-400 hover:bg-red-900/30 transition-all duration-150 active:scale-90 flex-shrink-0"
        >
          {unlinking
            ? <div className="w-3.5 h-3.5 border-2 border-red-500/50 border-t-red-500 rounded-full animate-spin"/>
            : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          }
        </button>
      )}
    </div>
  );
}

function ConnectionSlot({ connection, onUnlink, unlinking }) {
  const ROLE_LABELS = {
    mentor: "Mentor",
    therapist: "Therapeut",
    lernbegleiter: "Lernbegleiter",
    other: "Sonstige Person",
    teacher: "Lehrperson",
  };
  return (
    <div className="relative flex items-center gap-3 rounded-2xl px-4 py-3 bg-gradient-to-r from-cyan-950/30 to-cyan-900/10 border border-cyan-500/20 transition-all duration-200">
      <div className="w-9 h-9 rounded-xl bg-cyan-500/15 text-cyan-300 flex items-center justify-center text-sm font-black flex-shrink-0">
        {connection.avatar || "👤"}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-black text-white truncate">{connection.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" style={{ boxShadow: "0 0 4px #22d3ee" }}/>
          <p className="text-[10px] text-gray-500 font-medium">
            {ROLE_LABELS[connection.role] || connection.role} · {new Date(connection.linkedAt).toLocaleDateString("de-DE")}
          </p>
        </div>
      </div>
      <button
        onClick={() => onUnlink(connection.linkId)}
        disabled={unlinking}
        title="Verbindung entfernen"
        className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-600 hover:text-red-400 hover:bg-red-900/30 transition-all duration-150 active:scale-90 flex-shrink-0"
      >
        {unlinking
          ? <div className="w-3.5 h-3.5 border-2 border-red-500/50 border-t-red-500 rounded-full animate-spin"/>
          : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        }
      </button>
    </div>
  );
}

function ConnectionSection() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const [activeCodeId, setActiveCodeId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const parentsQuery = useQuery({
    queryKey: ["my-parents"],
    queryFn: () => api.get("/students/me/parents").then((r) => r.data),
    enabled: user?.role === "student",
  });

  const connectionsQuery = useQuery({
    queryKey: ["my-connections"],
    queryFn: () => api.get("/students/me/connections").then((r) => r.data),
    enabled: user?.role === "student",
  });

  const cancelInvite = useMutation({
    mutationFn: ({ id, type }) =>
      type === "parent"
        ? api.delete(`/students/me/parents/invite/${id}`)
        : api.delete(`/students/me/connections/invite/${id}`),
    onSuccess: () => {
      parentsQuery.refetch();
      connectionsQuery.refetch();
      setActiveCodeId(null);
      toast("Einladung zurückgezogen");
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler beim Zurückziehen"),
  });

  const unlinkParent = useMutation({
    mutationFn: (linkId) => api.delete(`/students/me/parents/${linkId}`),
    onSuccess: () => {
      parentsQuery.refetch();
      qc.invalidateQueries({ queryKey: ["student", "me"] });
      toast.success("Elternteil entfernt");
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler beim Entfernen"),
  });

  const unlinkConnection = useMutation({
    mutationFn: (linkId) => api.delete(`/students/me/connections/${linkId}`),
    onSuccess: () => {
      connectionsQuery.refetch();
      toast.success("Verbindung entfernt");
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler beim Entfernen"),
  });

  if (user?.role !== "student") return null;

  const parents = parentsQuery.data?.parents || [];
  const parentInvites = parentsQuery.data?.pendingInvites || [];
  const connections = connectionsQuery.data?.connections || [];
  const connectionInvites = connectionsQuery.data?.pendingInvites || [];
  const isLoading = parentsQuery.isLoading || connectionsQuery.isLoading;

  const totalParentSlots = 2;
  const usedParentSlots = parents.length + parentInvites.length;

  return (
    <>
      <div className="rounded-2xl overflow-hidden" style={{
        background: "linear-gradient(145deg, rgba(12,8,30,0.95), rgba(8,6,22,0.98))",
        border: "1px solid rgba(139,92,246,0.2)",
        boxShadow: "0 0 40px rgba(139,92,246,0.04)",
      }}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.25)" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#a78bfa" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-black text-white">Meine Verbindungen</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">
              {parents.length + connections.length === 0 && parentInvites.length + connectionInvites.length === 0
                ? "Lade Eltern, Mentoren und weitere Personen ein"
                : `${parents.length + connections.length} verbunden · ${parentInvites.length + connectionInvites.length} ausstehend`
              }
            </p>
          </div>
          <div className="flex items-center gap-1">
            {[0, 1].map((i) => (
              <div key={i} className={clsx(
                "w-2 h-2 rounded-full transition-all duration-300",
                i < parents.length ? "bg-violet-400" : i < usedParentSlots ? "bg-amber-400" : "bg-white/10"
              )} style={i < parents.length ? { boxShadow: "0 0 4px #a78bfa" } : undefined}/>
            ))}
            <span className="text-[10px] text-gray-600 font-bold ml-1">{parents.length}/2</span>
          </div>
        </div>

        <div className="px-4 py-4 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-6">
              <div className="w-5 h-5 rounded-full border-2 border-t-violet-500 border-r-cyan-500 border-b-violet-500/20 border-l-cyan-500/20 animate-spin"/>
              <span className="text-xs text-gray-600 font-medium">Lade…</span>
            </div>
          ) : (
            <>
              {/* ── Eltern ── */}
              {(parents.length > 0 || parentInvites.length > 0) && (
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Eltern</p>
                  {[0, 1].map((i) => (
                    <ParentSlot
                      key={i}
                      index={i}
                      parent={parents[i] || null}
                      onUnlink={(linkId) => { sfxClick(); unlinkParent.mutate(linkId); }}
                      unlinking={unlinkParent.isPending}
                    />
                  ))}
                  {parentInvites.map((inv) => (
                    <PendingInviteCard
                      key={inv.id}
                      inv={inv}
                      label="Elternteil eingeladen"
                      onCancel={(id) => { sfxClick(); cancelInvite.mutate({ id, type: "parent" }); }}
                      onToggleCode={() => setActiveCodeId(activeCodeId === inv.id ? null : inv.id)}
                      showingCode={activeCodeId === inv.id}
                      cancelling={cancelInvite.isPending && cancelInvite.variables?.id === inv.id}
                    />
                  ))}
                </div>
              )}

              {/* ── Weitere Verbindungen ── */}
              {(connections.length > 0 || connectionInvites.length > 0) && (
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Weitere</p>
                  {connections.map((c) => (
                    <ConnectionSlot
                      key={c.linkId}
                      connection={c}
                      onUnlink={(linkId) => { sfxClick(); unlinkConnection.mutate(linkId); }}
                      unlinking={unlinkConnection.isPending}
                    />
                  ))}
                  {connectionInvites.map((inv) => (
                    <PendingInviteCard
                      key={inv.id}
                      inv={inv}
                      label={`${inv.connectedRole ? (inv.connectedRole.charAt(0).toUpperCase() + inv.connectedRole.slice(1)) : "Person"} eingeladen`}
                      onCancel={(id) => { sfxClick(); cancelInvite.mutate({ id, type: "connection" }); }}
                      onToggleCode={() => setActiveCodeId(activeCodeId === inv.id ? null : inv.id)}
                      showingCode={activeCodeId === inv.id}
                      cancelling={cancelInvite.isPending && cancelInvite.variables?.id === inv.id}
                    />
                  ))}
                </div>
              )}

              {/* Empty state */}
              {parents.length === 0 && connections.length === 0 && parentInvites.length === 0 && connectionInvites.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-gray-600 text-xs">Noch niemand verbunden</p>
                </div>
              )}

              {/* Add CTA */}
              <button
                onClick={() => { sfxClick(); setAddModalOpen(true); }}
                className="w-full group flex items-center justify-center gap-2.5 py-3 rounded-2xl font-black text-sm transition-all duration-200 active:scale-[0.98]"
                style={{
                  background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(6,182,212,0.10))",
                  border: "1px solid rgba(139,92,246,0.35)",
                  color: "#c4b5fd",
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/>
                  <line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
                <span>+ Jemanden hinzufügen</span>
              </button>

              {/* How it works */}
              <div className="rounded-xl px-3 py-2.5 space-y-1.5"
                style={{ background: "rgba(6,182,212,0.04)", border: "1px solid rgba(6,182,212,0.1)" }}>
                <p className="text-[10px] font-black text-cyan-500 uppercase tracking-widest">So funktioniert es</p>
                {[
                  "Drück auf \"Hinzufügen\" → wähle eine Rolle",
                  "Du bekommst einen Code zum Teilen",
                  "Die Person registriert sich mit diesem Code",
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[9px] font-black text-cyan-600 mt-px w-3 flex-shrink-0">{i + 1}.</span>
                    <p className="text-[10px] text-gray-600 leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <AddConnectionModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreated={() => {
          parentsQuery.refetch();
          connectionsQuery.refetch();
        }}
      />
    </>
  );
}

/* ─── Child Connection Section (non-students) ───────────────────────────── */
function ChildConnectionSection() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [showInput, setShowInput] = useState(false);

  if (!user || user.role === "student" || user.role === "admin") return null;

  const { data: linkedData, isLoading: linkedLoading, refetch: refetchLinked } = useQuery({
    queryKey: ["linked-students"],
    queryFn: () => api.get("/students/me/linked-students").then((r) => r.data),
  });

  const redeemCode = useMutation({
    mutationFn: () => api.post("/students/me/redeem-connection", { code: code.replace(/-/g, "") }).then((r) => r.data),
    onSuccess: () => {
      sfxClick();
      setCode("");
      setShowInput(false);
      refetchLinked();
      qc.invalidateQueries({ queryKey: ["linked-students"] });
      toast.success("Erfolgreich verknüpft! 🎉");
    },
    onError: (err) => toast.error(err.response?.data?.error || "Code ungültig oder abgelaufen"),
  });

  const formatCode = (val) => {
    const cleaned = val.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 12);
    const parts = cleaned.match(/.{1,4}/g) || [];
    return parts.join("-");
  };

  const linkedStudents = linkedData?.students || [];

  return (
    <div className="rounded-2xl overflow-hidden" style={{
      background: "linear-gradient(145deg, rgba(12,8,30,0.95), rgba(8,6,22,0.98))",
      border: "1px solid rgba(6,182,212,0.2)",
      boxShadow: "0 0 40px rgba(6,182,212,0.04)",
    }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.25)" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#22d3ee" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-black text-white">Schüler/Kind verknüpfen</h3>
          <p className="text-[10px] text-gray-500 mt-0.5">
            {linkedStudents.length === 0
              ? "Gib den Code ein, den der Schüler erstellt hat"
              : `${linkedStudents.length} verknüpft${linkedStudents.length === 1 ? "" : "e"} Schüler`
            }
          </p>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {linkedLoading ? (
          <div className="flex items-center justify-center gap-2 py-4">
            <div className="w-5 h-5 rounded-full border-2 border-t-cyan-500 border-r-cyan-500/30 border-b-cyan-500/20 border-l-cyan-500/10 animate-spin"/>
            <span className="text-xs text-gray-600 font-medium">Lade…</span>
          </div>
        ) : (
          <>
            {/* Linked students list */}
            {linkedStudents.map((s) => (
              <div key={s.id} className="flex items-center gap-3 rounded-2xl px-4 py-3 bg-gradient-to-r from-cyan-950/30 to-cyan-900/10 border border-cyan-500/20">
                <div className="w-9 h-9 rounded-xl bg-cyan-500/15 text-cyan-300 flex items-center justify-center text-sm font-black flex-shrink-0">
                  {s.avatar || "🎓"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-white truncate">{s.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" style={{ boxShadow: "0 0 4px #22d3ee" }}/>
                    <p className="text-[10px] text-gray-500 font-medium">
                      Schüler · {new Date(s.linkedAt).toLocaleDateString("de-DE")}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Code input */}
            {showInput ? (
              <div className="space-y-2">
                <input
                  value={code}
                  onChange={(e) => setCode(formatCode(e.target.value))}
                  placeholder="XXXX-XXXX-XXXX"
                  maxLength={14}
                  autoFocus
                  className="w-full bg-nexus-s2 border border-nexus-border rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors text-center font-black tracking-widest"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowInput(false); setCode(""); }}
                    className="flex-1 py-2.5 rounded-xl bg-nexus-s2 border border-nexus-border text-gray-400 text-sm font-bold hover:text-white transition-all"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={() => redeemCode.mutate()}
                    disabled={code.replace(/-/g, "").length < 8 || redeemCode.isPending}
                    className="flex-1 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-black transition-all active:scale-95 flex items-center justify-center gap-2"
                  >
                    {redeemCode.isPending ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
                    ) : "Verknüpfen"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { sfxClick(); setShowInput(true); }}
                className="w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl font-black text-sm transition-all duration-200 active:scale-[0.98]"
                style={{
                  background: "linear-gradient(135deg, rgba(6,182,212,0.18), rgba(139,92,246,0.10))",
                  border: "1px solid rgba(6,182,212,0.35)",
                  color: "#67e8f9",
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/>
                  <line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
                <span>Schüler/Kind mit Code verknüpfen</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Main Settings Component ───────────────────────────────────────────── */
export default function Settings() {
  const { user, logout, updateSettings } = useAuthStore();
  const navigate = useNavigate();
  const settings = user?.settings || {};

  const [notifPush,    setNotifPush]    = useState(settings.notifsEnabled ?? true);
  const [soundOn,      setSoundOn]      = useState(settings.soundEnabled  ?? true);
  const [darkMode,     setDarkMode]     = useState(settings.darkMode      ?? true);
  const [language,     setLanguage]     = useState(settings.language      || "de");
  const [replayAnim,   setReplayAnim]   = useState(false);

  const save = useMutation({
    mutationFn: () => api.patch("/users/me/settings", {
      notifsEnabled: notifPush,
      soundEnabled:  soundOn,
      darkMode,
      language,
    }),
    onSuccess: (res) => {
      updateSettings(res.data.settings);
      setSoundEnabled(soundOn);
      toast.success("Einstellungen gespeichert");
    },
    onError: () => toast.error("Fehler beim Speichern"),
  });

  const handleReplayAnim = () => {
    setReplayAnim(true);
    // Trigger animation – App listens to this via localStorage flag
    localStorage.setItem("studox-replay-launch", "1");
    window.dispatchEvent(new Event("studox-replay-launch"));
    setTimeout(() => setReplayAnim(false), 6000);
    toast("Launch-Animation wird abgespielt…", { icon: "🚀" });
  };

  const LANGUAGES = [
    { v: "de", l: "Deutsch",  flag: "DE" },
    { v: "en", l: "English",  flag: "EN" },
    { v: "tr", l: "Türkçe",   flag: "TR" },
  ];

  return (
    <div className="space-y-4 animate-fade-in pb-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-black flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a1 1 0 0 0-1.41 0l-1.12 1.12a7 7 0 0 0-9.08 9.08L4.93 17.66a1 1 0 0 0 0 1.41 1 1 0 0 0 1.41 0L7.46 17.9A7 7 0 0 0 16.54 8.82l2.53-2.48a1 1 0 0 0 0-1.41z"/>
          </svg>
          Einstellungen
        </h1>
        <p className="text-xs text-gray-500 mt-0.5">Passe StudoX Flow nach deinen Wünschen an</p>
      </div>

      {/* Account */}
      <SectionCard title="Mein Account" icon={
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
      }>
        {[
          { k: "Name",  v: user?.name },
          { k: "Rolle", v: user?.role === "student" ? "Schüler" : user?.role === "teacher" ? "Lehrperson" : user?.role === "admin" ? "Administrator" : user?.role === "mentor" ? "Mentor" : "Elternteil" },
          { k: "PLZ",   v: user?.plz || "—" },
        ].map(({ k, v }) => (
          <div key={k} className="flex items-center justify-between py-2.5">
            <span className="text-xs text-gray-500 font-bold">{k}</span>
            <span className="text-sm font-bold text-white">{v}</span>
          </div>
        ))}
      </SectionCard>

      {/* Notifications */}
      <SectionCard title="Benachrichtigungen" icon={
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
      }>
        <SettingRow label="Push-Benachrichtigungen" sub="XP, Nachrichten, Quests" value={notifPush} onChange={setNotifPush}/>
      </SectionCard>

      {/* App */}
      <SectionCard title="App-Einstellungen" icon={
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
      }>
        <SettingRow
          label="Sound-Effekte"
          sub="Belohnungen, Antworten, Navigation"
          value={soundOn}
          onChange={(v) => { setSoundOn(v); setSoundEnabled(v); }}
        />
        <SettingRow
          label="Dunkles Design"
          sub="StudoX Dark Mode (empfohlen)"
          value={darkMode}
          onChange={setDarkMode}
          disabled
        />
      </SectionCard>

      {/* Lernwelten (students only) — controls the adaptive learner model's age */}
      {user?.role === "student" && <LearnerAgeSection />}

      {/* Connections (students only) */}
      {user?.role === "student" && <ConnectionSection />}

      {/* Child connection (non-students, non-admins) */}
      {user?.role !== "student" && user?.role !== "admin" && <ChildConnectionSection />}

      {/* Language */}
      <SectionCard title="Sprache" icon={
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
      }>
        <div className="py-3 flex gap-2">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.v}
              onClick={() => { sfxClick(); setLanguage(lang.v); }}
              className={clsx(
                "flex-1 py-2 rounded-xl text-xs font-bold border transition-all duration-200",
                language === lang.v
                  ? "bg-violet-600 border-violet-500 text-white"
                  : "bg-nexus-s2 border-nexus-border text-gray-500 hover:text-gray-300"
              )}
            >
              <span className="text-[10px] text-gray-500 block mb-0.5">{lang.flag}</span>
              {lang.l}
            </button>
          ))}
        </div>
      </SectionCard>

      {/* Gimmicks */}
      <SectionCard title="Extras" icon={
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
      }>
        <div className="py-3">
          <button
            onClick={handleReplayAnim}
            disabled={replayAnim}
            className={clsx(
              "w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-200",
              replayAnim
                ? "bg-violet-900/20 border-violet-500/40 text-violet-400 cursor-not-allowed"
                : "bg-nexus-s2 border-nexus-border text-gray-300 hover:border-violet-500/40 hover:text-violet-300"
            )}
          >
            <div className="flex items-center gap-2.5">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              <div className="text-left">
                <p className="text-xs font-bold">Launch-Animation erneut abspielen</p>
                <p className="text-[10px] text-gray-600 mt-0.5">Einmalig beim ersten Login</p>
              </div>
            </div>
            {replayAnim && (
              <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor"><circle cx="12" cy="12" r="10" opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
            )}
          </button>
        </div>
      </SectionCard>

      {/* Save */}
      <Button full loading={save.isPending} onClick={() => { sfxClick(); save.mutate(); }}>
        Einstellungen speichern
      </Button>

      {/* Danger zone */}
      <div className="bg-red-950/20 border border-red-900/30 rounded-2xl p-4">
        <h3 className="text-xs font-black text-red-400 uppercase tracking-widest mb-3 flex items-center gap-2">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          Abmelden
        </h3>
        <p className="text-[11px] text-gray-500 mb-3">Du kannst dich jederzeit mit deinen Zugangsdaten erneut einloggen.</p>
        <button
          onClick={() => { logout(); toast("Bis zum nächsten Mal!"); }}
          className="w-full py-2.5 rounded-xl bg-red-900/40 border border-red-800/40 text-red-300 text-xs font-bold hover:bg-red-800/50 transition-colors flex items-center justify-center gap-2"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Abmelden
        </button>
      </div>

      {/* StudoX Core Hub */}
      <button
        onClick={() => { sfxClick(); launchCore(); }}
        className="w-full relative overflow-hidden rounded-2xl border transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group"
        style={{
          borderColor: "rgba(139,92,246,0.25)",
          background: "radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.1) 0%, rgba(8,8,24,0.9) 70%)",
        }}
      >
        <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: "linear-gradient(90deg, transparent, #8b5cf6, #06b6d4, transparent)" }} />
        <div className="flex items-center gap-4 p-4">
          {/* S icon */}
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, rgba(139,92,246,0.3), rgba(6,182,212,0.2))",
              border: "1px solid rgba(139,92,246,0.3)",
              boxShadow: "0 0 12px rgba(139,92,246,0.2)",
            }}
          >
            <span
              className="text-lg font-black"
              style={{ background: "linear-gradient(135deg, #c084fc, #06b6d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
            >S</span>
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-black text-white">StudoX Core</p>
            <p className="text-[11px] text-gray-500 mt-0.5">Alle StudoX-Apps auf einen Blick</p>
          </div>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2"
            stroke="rgba(139,92,246,0.6)" strokeLinecap="round" strokeLinejoin="round"
            className="flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1"
          >
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      </button>

      <div className="pb-2" />
    </div>
  );
}
